import { NextResponse } from 'next/server';

/**
 * Server Action requests that arrive without a usable session (Wattle CHSP
 * readiness P1-11, 4 Nov 2026).
 *
 * The login redirect used to answer a Server Action POST with a 307 to /login.
 * The browser followed it, re-posted the action to the login page, and the
 * Save button's promise never settled into anything the form understood, so
 * the spinner hung and typed work was lost on the next navigation.
 *
 * Middleware now answers such a request with a 401 `text/plain` body. Next's
 * action client turns a >=400 `text/plain` response into a rejected action
 * carrying this exact message, and the marker header lets the dashboard's
 * SessionExpiredActionGuard keep the page (and its draft) in place while the
 * user signs in again in another tab. The action never ran, so retrying it
 * after sign-in is safe.
 */
export const SESSION_EXPIRED_ACTION_HEADER = 'x-statura-session';
export const SESSION_EXPIRED_ACTION_HEADER_VALUE = 'expired';
export const SESSION_EXPIRED_ACTION_MESSAGE =
  'Your session expired. Sign in to continue; your changes have not been saved yet.';

export function isServerActionRequest(request: {
  method: string;
  headers: { has(name: string): boolean };
}): boolean {
  return request.method === 'POST' && request.headers.has('next-action');
}

export function sessionExpiredActionResponse(): NextResponse {
  return new NextResponse(SESSION_EXPIRED_ACTION_MESSAGE, {
    status: 401,
    headers: {
      // Exactly `text/plain`: Next only surfaces the body as the error message
      // when the content type matches without parameters.
      'content-type': 'text/plain',
      'cache-control': 'no-store',
      [SESSION_EXPIRED_ACTION_HEADER]: SESSION_EXPIRED_ACTION_HEADER_VALUE,
    },
  });
}

/**
 * A Server Action refused before it runs for a reason a fresh sign-in in
 * another tab cannot fix (deactivated account, administrator MFA required).
 * No marker header, so the guard does not hold it: the action rejects at once
 * with this message instead of following a redirect and hanging.
 */
export function blockedActionResponse(message: string): NextResponse {
  return new NextResponse(message, {
    status: 403,
    headers: { 'content-type': 'text/plain', 'cache-control': 'no-store' },
  });
}

export const ACCOUNT_DEACTIVATED_ACTION_MESSAGE =
  'Your account has been deactivated, so this was not saved. Contact your administrator.';
export const MFA_REQUIRED_ACTION_MESSAGE =
  'Two-factor sign-in is required before this can be saved. Reload the page to set it up.';

/** Login URL for the "sign in again" link: back to this page, with the reason. */
export function sessionExpiredLoginHref(currentPathWithSearch: string): string {
  const next =
    currentPathWithSearch.startsWith('/') && !currentPathWithSearch.startsWith('//')
      ? currentPathWithSearch
      : '/';
  return `/login?reason=session_expired&next=${encodeURIComponent(next)}`;
}
